<template>
  <i
    class="tm-visual"
    :class="{ '-sem': MODE_SCENE_EDIT }"
    @dblclick="$_editVisual"
  >
    <tm-visual-span
      v-for="(rectangle, i) in trackRectangles"
      :key="i"
      :track="$props.track"
      :rectangle="rectangle"
      :isSelected="isSelected"
      :isFirst="i === 0"
      :isLast="i === trackRectangles.length - 1"
      :isHovered="$data.isHovered"
      :showBorder="showBorder"
      :isSceneEdit="isSceneEdit"
      :videoTranscript="videoTranscript"
      :scenes="scenes"
      @hovered="$_onHover"
    />

    <template v-if="showOverlays">
      <tm-overlay
        v-for="overlay in relevantOverlayTracks"
        :key="overlay.guid"
        :pipa="relevantPipa"
        :overlay="overlay"
        :isSceneEdit="isSceneEdit"
        @hoveredOverlay="$_onHoverOverlay"
      />
    </template>

    <tm-visual-resize
      :visible="showResize"
      :fromLeft="true"
      :track="$props.track"
      :isSelected="isSelected"
      @hovered="$_onHover"
    />

    <tm-visual-resize
      :visible="showResize"
      :fromRight="true"
      :track="$props.track"
      :isSelected="isSelected"
      @hovered="$_onHover"
    />
  </i>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { getColourCardCss } from '../../../api/asset';
import gridMixin from '../../grid.mixin';
import tmVisualSpan from './tmVisualSpan.vue';
import tmVisualResize from './tmVisualResize.vue';
import tmOverlay from './overlay/tmOverlay.vue';
import tmBus from '../../../api/tmBus';

export default {
  name: 'tm-visual',
  mixins: [gridMixin],
  components: {
    tmVisualSpan,
    tmVisualResize,
    tmOverlay,
  },

  props: {
    track: { type: Object, required: true },
  },

  data() {
    return {
      isHovered: false,
      scenes: [],
    };
  },

  created() {
    this.createScenes();
  },

  computed: {
    ...mapState('timemachine', {
      MODE_SCENE_EDIT: (state) => state.MODE.SCENE_EDIT,
      MODE_OPTIONAL_AUDIO_INSERT: (state) => state.MODE.OPTIONAL_AUDIO_INSERT,
      MODE_AUDIO_MULTISELECT: (state) => state.MODE.AUDIO_MULTISELECT,
      selectedVisual: (state) => state.selected.visual,
      sceneEscort: (state) => state.sceneEscort,
    }),
    ...mapGetters('timemachine', ['isDndMode']),
    ...mapState({
      storyTranscriptions: (state) => state.story.transcriptions,
    }),

    isSelected() {
      return (
        !!this.selectedVisual &&
        this.selectedVisual.guid === this.$props.track.guid
      );
    },

    isSceneEdit() {
      return this.sceneEscort.sceneTrack?.guid === this.$props.track.guid;
    },

    videoTranscript() {
      return (
        this.storyTranscriptions[this.$props.track.data.metadata.assetUUID] ??
        null
      );
    },

    wallpaperMetaData() {
      return this.$props.track.data.wallpaperMetaData;
    },

    /** @note: additional dependency on `grid` defined in gridMixin */
    trackRectangles() {
      if (this.gridEstablished) {
        // console.log('trackRectangles ⟹ realtime');
        return this.$_time2rectangles(
          this.$props.track.data.displayStartTime,
          this.$props.track.data.displayEndTime,
          true
        );
      } else {
        // console.log('trackRectangles ⟹ cache');
        // while initializing grid - keep using old coordinates
        return this.$props.track.rectangles;
      }
    },

    showBorder() {
      return this.isSelected && !this.MODE_SCENE_EDIT;
    },

    showResize() {
      return (
        this.$props.track.isResizing ||
        (this.$data.isHovered &&
          !this.isDndMode &&
          !this.MODE_SCENE_EDIT &&
          !this.MODE_OPTIONAL_AUDIO_INSERT &&
          !this.MODE_AUDIO_MULTISELECT)
      );
    },

    showOverlays() {
      return (
        !this.relevantPipa.isEmpty() && (this.isSceneEdit || this.isSelected)
      );
    },

    relevantPipa() {
      return this.isSceneEdit
        ? this.sceneEscort.actingPipa
        : this.$props.track.pipa;
    },

    relevantOverlayTracks() {
      if (this.$props.track.isResizing) {
        return this.$props.track.pipa.getCopyOfTracksDuringResize(
          this.$props.track.resizeEscort
        );
      } else {
        return this.relevantPipa.track;
      }
    },
  },

  watch: {
    // assign rectangles to this.$props.track for cache access
    trackRectangles: {
      immediate: true,
      handler(value) {
        this.$props.track.rectangles = value;
      },
    },

    wallpaperMetaData: {
      immediate: true,
      deep: true,
      handler(value) {
        if (value) {
          this.$props.track.colourCardCss = getColourCardCss(
            value.wallpaperData
          );
        }
      },
    },
  },

  methods: {
    ...mapActions('timemachine', ['UNSELECT_TRACKS']),

    createScenes() {
      const isRelevant =
        app.assetType.isFRAGMENT(this.$props.track.data) &&
        !app.assetType.isSOCIALFRAGMENT(this.$props.track.data);
      if (isRelevant) {
        app.rootStore
          .dispatch('FETCH_SCENES', {
            assetId: this.$props.track.data.metadata.assetUUID,
          })
          .then((scenes) => {
            this.scenes = _.filter(
              scenes,
              (scene) => scene.duration >= app.TIME.MIN_SCENE_DURATION
            );
          });
      }
    },

    $_onHover(hovered, rectangle) {
      if (!this.isDndMode) {
        this.$data.isHovered = hovered;
        if (hovered) {
          this.$emit('hoveredRectangle', rectangle);
        }
      }
    },

    $_onHoverOverlay(hovered, overlayRect) {
      this.$data.isHovered = hovered;
      if (hovered) {
        const index = _.findIndexInSorted(this.trackRectangles, (trackRect) => {
          return overlayRect.startTime < trackRect.startTime
            ? -1
            : overlayRect.startTime < trackRect.endTime
            ? 0
            : 1;
        });
        const trackRectangle = this.trackRectangles[index];
        this.$emit('hoveredRectangle', trackRectangle);
      }
    },

    $_editVisual: _.debounce(
      function (e) {
        tmBus.$emit('callbackEditAsset', {
          asset: this.$props.track.data,
          overlayGuid: null,
        });
      },
      256,
      { leading: true, trailing: false }
    ),
  },
};
</script>

<style lang="scss">
.tm-tracks-visual > .tm-visual {
  position: absolute;
  top: 0;

  cursor: grab;
  &.-sem {
    cursor: default;
  }
}
</style>
